import * as React from 'react';
import { DeleteOutlined, PlusOutlined } from '@easyv/react-icons';
import { Empty } from '@easyv/react-components';
import { Component, RuntimeScene, VirtualChildComponent } from '@easytwin/runtime';
import { UISchema, UISchemaFunc } from '@/type';
import { deepClone, isNil } from '@/utils';
import { UIGenerator } from '@/UIGenerator';
import { CollapseBox } from '../CollapseBox';
import { SortableTabs, SortableTabsDataSourceType } from './SortableTabs';
import classNames from 'classnames';
import { useControlledState } from './util';

interface TabsProps<T extends { id: string; name?: string }> {
  comp: Component | VirtualChildComponent | RuntimeScene;
  allowEmpty?: boolean;
  disableFold?: boolean;
  title: string;
  name: string;
  value: T[];
  childSchema: UISchema | UISchemaFunc;
  /** 是否显示连接线 */
  showConnectionLine?: boolean;
  /** 添加按钮触发后新增的数据 */
  onAddItem: () => T;
  onChange?: (name: string, value: T[]) => void;
  onConfirm: (name: string, value: T[]) => void;
  /** 是否显示按钮 */
  showActions?: boolean;
  /** 是否使用value的name作为tab名称 */
  useValueName?: boolean;
  /** 切换tab时触发 */
  onTabChange?: (index: number | undefined, id: string | undefined) => void;
  /** 覆写内部的添加逻辑（需要自行处理数据的保存和同步, item为onAddItem的返回值 */
  onItemAdd?: (item: T) => void;
  /** 覆写内部的添加逻辑（需要自行处理数据的保存和同步）*/
  onItemRemove?: (item: T) => void;

  // 受控模式
  selectKey?: string;
  setSelectKey?: (key: string | undefined) => void;
}

export function Tabs<T extends { id: string; name?: string }>(props: TabsProps<T>) {
  const {
    comp,
    allowEmpty = false,
    disableFold,
    name,
    title,
    value = [],
    childSchema,
    onChange,
    onConfirm,
    onAddItem,
    showActions = true,
    useValueName = true,
    showConnectionLine = true,
    onTabChange,
    onItemAdd,
    onItemRemove,
    selectKey: _selectKey,
    setSelectKey: _setSelectKey,
  } = props;
  const isControlledMode = _setSelectKey !== undefined;
  const [selectKey, setSelectKey] = useControlledState<string>(_selectKey, _setSelectKey);
  const [renamingStateId, setRenamingStateId] = React.useState<string>();

  const handleTabChange = (id: string | undefined) => {
    setSelectKey?.(id);
    const newIndex = value.findIndex((item) => item.id === id);
    if (onTabChange) {
      onTabChange(newIndex, id);
    }
  };

  React.useEffect(() => {
    // 如果没选中，或者选中了新的同类型的组件，则默认选中第一个
    if (allowEmpty) return;
    if (selectKey === undefined || !value.find((a) => a.id === selectKey)) {
      value.length > 0 && handleTabChange(value[0].id);
    }
  }, [selectKey, value, allowEmpty]);

  const selectedItem = React.useMemo(() => {
    if (!Array.isArray(value)) {
      throw new Error(`Tabs组件需要传入数组,但是得到${JSON.stringify(value)}`);
    }
    return value.find((a) => a.id === selectKey);
  }, [selectKey, value]);

  console.log('selected item', selectedItem);
  const schema = typeof childSchema === 'function' ? childSchema(comp, selectedItem) : childSchema;

  const handleAddItem = () => {
    const item = onAddItem();
    if (onItemAdd) {
      onItemAdd(item);
    } else {
      const newData = deepClone(value);
      newData.push(item);
      onConfirm(name, newData);
    }
    !isControlledMode && handleTabChange(item.id);
  };

  const handleRemoveItem = () => {
    const index = value.findIndex((a) => a.id === selectKey);
    if (index === -1) return;
    if (onItemRemove) {
      onItemRemove(value[index]);
    } else {
      const newData = deepClone(value);
      newData.splice(index, 1);
      onConfirm(name, newData);
    }

    !isControlledMode && handleTabChange(undefined);
  };

  const handleItemChange = (v: T) => {
    const index = value.findIndex((a) => a.id === selectKey);
    if (index === -1) return;
    const newData = deepClone(value);
    newData[index] = v;
    onChange?.(name, newData);
  };

  const handleItemConfirm = (v: T) => {
    const index = value.findIndex((a) => a.id === selectKey);
    if (index === -1) return;
    const newData = deepClone(value);
    newData[index] = v;
    onConfirm(name, newData);
  };

  const handleRenameStart = (id: string) => {
    setRenamingStateId(id);
  };

  const handleRenameStateEnd = (v: string) => {
    if (!renamingStateId) return;
    const index = value.findIndex((a) => a.id === selectKey);
    if (index === -1) return;
    const newData = deepClone(value);
    newData[index].name = v;
    onConfirm(name, newData);
    setRenamingStateId(undefined);
  };

  return (
    <CollapseBox
      disable={disableFold}
      showConnectionLine={value && value.length > 0 && showConnectionLine}
      title={title}
      titleExtra={
        showActions && (
          <>
            <PlusOutlined
              className='hover:text-white text-baseText p-1 text-base'
              onClick={(e) => {
                handleAddItem();
                e.stopPropagation();
              }}
            />
            <DeleteOutlined
              className='hover:text-white text-baseText p-1 text-base'
              onClick={(e) => {
                handleRemoveItem();
                e.stopPropagation();
              }}
            />
          </>
        )
      }
    >
      {value && value.length > 0 ? (
        <>
          <SortableTabs
            className={classNames(showConnectionLine && 'pl-4')}
            isSortable
            dataSource={value.map<SortableTabsDataSourceType>((a, index) => ({
              id: a.id,
              name: useValueName ? a.name ?? `内容${index}` : `内容${index}`,
              rank: index,
            }))}
            selectKey={selectKey}
            isCanRename={useValueName}
            renamingStateId={renamingStateId}
            onTabChange={handleTabChange}
            onRenameStateStart={handleRenameStart}
            onRenameStateEnd={handleRenameStateEnd}
          />
          {isNil(selectKey) ? (
            <Empty description={`未选中${title}`} className='py-8' />
          ) : (
            <UIGenerator
              comp={comp}
              schema={schema}
              json={selectedItem}
              onUpdate={handleItemChange}
              onConfirm={handleItemConfirm}
            />
          )}
        </>
      ) : (
        <div className='px-4 py-6 text-center text-textColorSecondary flex items-center justify-center'>
          <span>未配置{title}</span>
          {showActions && (
            <>
              <span>，点击</span>
              <PlusOutlined
                className='text-white hover:text-primary p-1 cursor-pointer'
                onClick={(e) => {
                  handleAddItem();
                  e.stopPropagation();
                }}
              />
              <span>创建</span>
            </>
          )}
        </div>
      )}
    </CollapseBox>
  );
}
